import { useState } from "react";
import { Image, Camera, Music, Mic, Square } from "lucide-react";
import { prettyBytes } from "../../cloudinary/upload";
import { AttachmentThumb, MediaLightbox } from "./AttachmentViewer";
import { useMediaUpload } from "./useMediaUpload";

export function AttachmentField({ value = [], onChange }) {
  const items = Array.isArray(value) ? value : [];
  const [active, setActive] = useState(null); // attachment shown full-screen
  const media = useMediaUpload({ onUploaded: (att) => onChange([...items, att]), count: items.length });
  const { configured, uploading, progress, error, recording, inputs, atLimit } = media;
  const busy = uploading || atLimit;

  const remove = (id) => onChange(items.filter((a) => a.id !== id));

  // Matches the add/edit panels' option pills (category, priority, …).
  const btn = { display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 13px", borderRadius: 20, fontSize: 14.5, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", color: "#a07090", background: "var(--c-bg-card)" };

  if (!configured) {
    return <p style={{ margin: 0, fontSize: 11.5, color: "var(--c-text-soft)", lineHeight: 1.5 }}>Attachments turn on once Cloudinary is connected (add the env vars).</p>;
  }

  return (
    <div>
      {inputs}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        <button type="button" style={btn} onClick={media.openPhoto} disabled={busy}><Image size={14} /> Photo / Video</button>
        <button type="button" style={btn} onClick={media.openCamera} disabled={busy}><Camera size={14} /> Camera</button>
        <button type="button" style={btn} onClick={media.openAudio} disabled={busy}><Music size={14} /> Audio</button>
        {!recording
          ? <button type="button" style={btn} onClick={media.startRec} disabled={busy}><Mic size={14} /> Record</button>
          : <button type="button" style={{ ...btn, border: "none", color: "#fff", background: "linear-gradient(135deg,#e0608a,#d4789a)" }} onClick={media.stopRec}><Square size={13} fill="currentColor" /> Stop</button>}
      </div>

      {atLimit && !error && (
        <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--c-text-soft)" }}>
          Attachment limit reached ({media.limits.maxCount} per item).
        </p>
      )}
      {uploading && (
        <div style={{ marginTop: 8 }}>
          <div style={{ height: 6, borderRadius: 999, background: "var(--c-border)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", transition: "width 0.2s" }} />
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--c-text-soft)" }}>Uploading… {progress}%</p>
        </div>
      )}
      {error && <p style={{ margin: "6px 0 0", fontSize: 11, color: "#e0608a" }}>{error}</p>}

      {items.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          {items.map((a) => (
            <div key={a.id} style={{ position: "relative", width: a.kind === "audio" ? 200 : 84 }}>
              <button type="button" onClick={() => remove(a.id)} title="Remove"
                style={{ position: "absolute", top: -7, right: -7, zIndex: 2, width: 20, height: 20, borderRadius: "50%", border: "none", cursor: "pointer", background: "#e0608a", color: "#fff", fontSize: 12, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
              <AttachmentThumb a={a} onOpen={() => setActive(a)} />
              {a.bytes ? <p style={{ margin: "3px 0 0", fontSize: 9.5, color: "var(--c-text-soft)", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{prettyBytes(a.bytes)}</p> : null}
            </div>
          ))}
        </div>
      )}
      <MediaLightbox active={active} onClose={() => setActive(null)} />
    </div>
  );
}
